/**
 * Quest (user-facing) fields on the missions model.
 * Internal code name stays `mission`; UI copy uses Quest / Spot.
 */
import { haversineM, lineDistanceM } from "./geo.ts";
import type { LngLat } from "./types.ts";

export const SPOT_KINDS = ["start", "finish", "spot"] as const;
export type SpotKind = (typeof SPOT_KINDS)[number];

export const MAX_MISSION_TITLE = 80;
export const MAX_MISSION_DESCRIPTION = 2000;
export const MAX_MISSION_TAGLINE = 160;
export const MAX_MISSION_CITY = 80;
export const MAX_MISSION_COUNTRY = 80;
export const MAX_MISSION_STORY = 8000;
export const MAX_MISSION_MANNERS = 4000;
export const MAX_MISSION_COVER_URL = 2000;
export const MAX_MISSION_TAGS = 12;
export const MAX_MISSION_TAG_LEN = 32;
export const MAX_SPOTS = 20;
export const MAX_SPOT_TITLE = 80;
export const MAX_SPOT_LABEL = 2000;
export const MAX_SPOT_HIDDEN_STORY = 4000;
export const MAX_SPOT_POSE_HINT = 500;
export const MAX_SPOT_LINK_URL = 2000;
export const MIN_SPOT_RADIUS_M = 5;
export const MAX_SPOT_RADIUS_M = 500;
export const DEFAULT_SPOT_RADIUS_M = 35;
export const MIN_EST_MINUTES = 1;
export const MAX_EST_MINUTES = 24 * 60;

export function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export function parseTagsJson(raw: string | null | undefined): string[] {
  if (raw == null || raw === "") return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is string => typeof t === "string");
  } catch {
    return [];
  }
}

/** Straight-line sum between consecutive spots when there is no route line. */
export function spotPathDistanceM(spots: Array<{ lat: number; lng: number }>): number {
  let total = 0;
  for (let i = 1; i < spots.length; i++) {
    const a = spots[i - 1]!;
    const b = spots[i]!;
    total += haversineM([a.lng, a.lat], [b.lng, b.lat]);
  }
  return total;
}

export function questDistanceM(
  line: LngLat[] | null | undefined,
  spots: Array<{ lat: number; lng: number }>,
): number {
  if (line && line.length >= 2) return lineDistanceM(line);
  return spotPathDistanceM(spots);
}

export function sumEstMinutes(spots: Array<{ est_minutes: number | null | undefined }>): number {
  return spots.reduce((sum, s) => sum + (typeof s.est_minutes === "number" ? s.est_minutes : 0), 0);
}

/** A quest Description as stored: Windows line breaks unified, outer spaces trimmed. */
export function normalizeDescription(text: string): string {
  return text.replace(/\r\n?/g, "\n").trim();
}

export interface CheckpointThumbnailPrev {
  lat: number;
  lng: number;
  title: string | null;
  thumbnail_key: string | null;
}

export function matchCheckpointRowIndex(
  prev: CheckpointThumbnailPrev[],
  spot: Pick<CheckpointThumbnailPrev, "lat" | "lng" | "title">,
): number {
  const title = (spot.title ?? "").trim();
  let idx = prev.findIndex(
    (p) => p.lat === spot.lat && p.lng === spot.lng && (p.title ?? "").trim() === title,
  );
  if (idx >= 0) return idx;
  idx = prev.findIndex((p) => p.lat === spot.lat && p.lng === spot.lng);
  return idx;
}

/** Keep spot thumbnails when checkpoint order changes on save. */
export function mergeCheckpointThumbnailKey(
  prev: CheckpointThumbnailPrev[],
  spot: Pick<CheckpointThumbnailPrev, "lat" | "lng" | "title">,
  index: number,
): string | null {
  const title = (spot.title ?? "").trim();
  const byIdentity = prev.find(
    (p) => p.lat === spot.lat && p.lng === spot.lng && (p.title ?? "").trim() === title,
  );
  if (byIdentity?.thumbnail_key) return byIdentity.thumbnail_key;
  const byLocation = prev.find((p) => p.lat === spot.lat && p.lng === spot.lng);
  if (byLocation?.thumbnail_key) return byLocation.thumbnail_key;
  return prev[index]?.thumbnail_key ?? null;
}
